/**
 * Bring a tab into view inside a tab bar that scrolls sideways, by moving the
 * bar's scrollLeft and nothing else (compact chrome, 2026-10-10).
 *
 * The tab components used scrollIntoView({ block: 'nearest', inline:
 * 'nearest' }), which also scrolls every scrolling ancestor — <main> — to the
 * bar whenever the bar is below the fold. Measured: the project page jumped
 * 61px on load at 1280×720 and 220px on a phone.
 *
 * "Nearest", as before: a tab already in view does not move, one cut off at
 * either end comes in until it sits `margin` px inside the bar, and one wider
 * than the bar keeps its start in view. The browser clamps scrollLeft to the
 * bar's range.
 */
export function revealTab(bar: HTMLElement, tab: HTMLElement, margin = 8): void {
  const b = bar.getBoundingClientRect();
  const t = tab.getBoundingClientRect();
  const beforeStart = b.left + margin - t.left;
  const pastEnd = t.right - (b.right - margin);
  if (beforeStart > 0) bar.scrollLeft -= beforeStart;
  else if (pastEnd > 0) bar.scrollLeft += Math.min(pastEnd, -beforeStart);
}

/**
 * For a tab opened after the page loaded, above all from somewhere other than
 * its bar: the project page's metric tiles and Assign Crew, the machine page's
 * "View inspection", a link inside another tab (compact chrome review,
 * 2026-10-11). If the open tab cannot be seen — below the fold, scrolled past,
 * or under something drawn over the page: the NADC.AI button, a page's sticky
 * action bar, the project page's sticky bar — the bar goes to the top of the
 * page with what it opened below it. A tab in sight — one just clicked in its
 * bar, or a bar docked in the top row — stays where it is. Call it after
 * revealTab, so the tab is inside its bar.
 *
 * Not scrollIntoView's "nearest", which parks a bar from below on the bottom
 * edge. Measured at 390×844, that left the open tab under the NADC.AI button
 * on the project page, and the machine page's whole bar under its action bar.
 */
export function revealBar(bar: HTMLElement, tab: HTMLElement): void {
  const t = tab.getBoundingClientRect();
  const drawn = document.elementFromPoint(t.left + t.width / 2, t.top + t.height / 2);
  if (drawn && bar.contains(drawn)) return;
  bar.scrollIntoView({ block: 'start' });
}
